/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React from "react";
import PropTypes from "prop-types";

import { FROSTED_PANEL_STYLE } from "../../components/CompanyMetrics/constants";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";

/**
 * Modal shell for the TV View page.
 *
 * It borrows ONE thing from the metric dialogs — FROSTED_PANEL_STYLE, the
 * system's frosted-glass surface — so every popup in the app is the same
 * material. Size, header and footer are this modal's own: it holds a workspace
 * list and settings, so it needs room and a real confirm, not the 340px
 * one-button shell the read-only rules dialog uses.
 */

export default function TvViewModal({ open, title, subtitle, width = 520, onClose, onDone, doneLabel = "Done", children }) {
  useLockBodyScroll(open);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-900/15 backdrop-blur-[3px]"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="rounded-lg w-full flex flex-col max-h-[86vh] overflow-hidden"
        style={{ ...FROSTED_PANEL_STYLE, maxWidth: width }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 px-[22px] pt-[20px] pb-[14px]">
          <div>
            <h3 className="text-[16px] font-semibold text-gray-900 tracking-[-0.01em]">{title}</h3>
            {subtitle && <p className="text-[12.5px] text-gray-500 mt-[3px] leading-snug">{subtitle}</p>}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-7 h-7 flex-shrink-0 rounded-full bg-[#F2F4F7] text-[#667085] flex items-center justify-center hover:bg-[#EAECF0] transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className="px-[22px] pb-[4px] overflow-y-auto">{children}</div>

        <div className="flex justify-end gap-2.5 px-[22px] pt-[16px] pb-[20px]">
          <button
            type="button"
            onClick={onClose}
            className="px-[18px] h-10 bg-white border border-[#D0D5DD] text-[#344054] rounded-lg font-medium text-[13px] hover:bg-[#F9FAFB] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDone || onClose}
            className="px-[20px] h-10 bg-[var(--gmb-primary)] text-white rounded-lg font-medium text-[13px] hover:brightness-95 transition-all"
          >
            {doneLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

TvViewModal.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  width: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  doneLabel: PropTypes.string,
  children: PropTypes.node,
};
